import React, { useMemo } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LabelList,
} from 'recharts';
import { COLOR_PALETTE, SPACER_VALUE } from './Constants';
import { formatUSDMillions } from '../../../utils/currency';

interface QSightHorizontalStackedChartProps {
    data: Array<Record<string, string | number>>;
    categoryKey: string;
}

interface TooltipProps {
    active?: boolean;
    payload?: {
        name: string;
        value: number;
        color: string;
        dataKey: string;
    }[];
    label?: string;
}

// Custom tooltip component for the chart
const CustomTooltip: React.FC<TooltipProps> = ({ active, payload, label }) => {
    // Show tooltip only if it's active and has valid payload
    if (active && payload && payload.length) {
        // Filter out spacer entries to avoid showing them in the tooltip
        const filteredPayload = payload.filter(entry => !entry.dataKey.startsWith('spacer_'));

        return (
            <div className="custom-tooltip" style={{ backgroundColor: '#fff', padding: 10, border: '1px solid #ccc' }}>
                {/* Display the label (usually the category name) */}
                <p className="label">{label}</p>

                {/* Display each data entry with its name and value */}
                {filteredPayload.map((entry, index) => (
                    <p key={index} style={{ color: entry.color }}>
                        {entry.name}: {formatUSDMillions(entry.value)}
                    </p>
                ))}
            </div>
        );
    }

    // Return nothing if tooltip is not active
    return null;
};

const QSightHorizontalStackedBarChart: React.FC<QSightHorizontalStackedChartProps> = ({
    data,
    categoryKey,
}) => {

    // Extract all unique keys from the data except the category and total
    const keys = useMemo(() => {
        const allKeys = new Set<string>();
        data.forEach(item => {
            Object.keys(item).forEach(key => {
                if (key !== categoryKey && key !== 'total') {
                    allKeys.add(key);
                }
            });
        });
        return Array.from(allKeys);
    }, [data, categoryKey]);

    // Add spacer keys between each actual key for visual separation
    const keysWithSpacers = useMemo(() => {
        const spacedKeys: string[] = [];
        keys.forEach((key, index) => {
            spacedKeys.push(key);
            if (index < keys.length - 1) {
                spacedKeys.push(`spacer_${index}`);
            }
        });
        return spacedKeys;
    }, [keys]);

    // Map each key to a color from the palette; spacers get transparent color
    const colorMap = useMemo(() => {
        const map: Record<string, string> = {};
        keys.forEach((key, index) => {
            map[key] = COLOR_PALETTE[index % COLOR_PALETTE.length];
            if (index < keys.length - 1) {
                map[`spacer_${index}`] = 'transparent';
            }
        });
        return map;
    }, [keys]);

    // Enhance data with total values and spacer entries
    const dataWithTotals = useMemo(() => {
        return data.map(entry => {
            const newEntry: Record<string, any> = { ...entry };

            // Calculate total value for the entry
            const total = keys.reduce((sum, key) => sum + (Number(entry[key]) || 0), 0);

            // Add spacer values
            keys.forEach((_, index) => {
                if (index < keys.length - 1) {
                    newEntry[`spacer_${index}`] = SPACER_VALUE;
                }
            });

            // Update category label to include total value
            return {
                ...newEntry,
                total,
                [categoryKey]: `${entry[categoryKey]} (${formatUSDMillions(total)})`,
            };
        });
    }, [data, keys, categoryKey]);

    return (
        <ResponsiveContainer width="100%" height={450}>
            <BarChart
                layout="vertical"
                data={dataWithTotals}
                margin={{ top: 20, right: 50, left: 100, bottom: 20 }}
                barSize={25}
            >
                {/* X-axis is numeric and hidden */}
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: 'transparent' }} />

                {/* Y-axis shows category labels */}
                <YAxis
                    dataKey={categoryKey}
                    type="category"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#333', fontWeight: 'bold' }}
                    width={150}
                />

                {/* Custom tooltip and legend */}
                <Tooltip content={<CustomTooltip />} />
                <Legend />

                {/* Render bars for each key and spacer */}
                {keysWithSpacers.map(key => (
                    <Bar
                        key={key}
                        dataKey={key}
                        stackId="HorizontalStackedBar"
                        fill={colorMap[key]}
                        isAnimationActive={false}
                    >
                        {/* Add labels only for actual data keys, not spacers */}
                        {!key.startsWith('spacer_') && (
                            <LabelList
                                dataKey={key}
                                position="top"
                                formatter={(value: number) => (value ? formatUSDMillions(value) : '')}
                                style={{ fill: '#000', fontSize: 12 }}
                            />
                        )}
                    </Bar>
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
};

export default QSightHorizontalStackedBarChart;
